'use client'

import {
  fontSizeMap,
  lineHeightMap,
  paddingMap,
  themeBackgroundMap,
  themeTextColorMap,
} from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'
import {
  type ChangeEvent,
  type KeyboardEvent,
  forwardRef,
  useState,
} from 'react'
import { IconButton } from '../buttons/icon-button.tsx'
import { Icon } from '../icon/icon.tsx'
import { useMultiselectContext } from './use-multiselect.ts'
import {
  searchInputFontSize,
  searchInputLeading,
  searchInputPadding,
} from './utils.ts'

type MultiselectSearchProps = {
  placeholder?: string
}

const MultiselectSearch = forwardRef<HTMLInputElement, MultiselectSearchProps>(
  ({ placeholder }, ref) => {
    const { onSearch, search } = useMultiselectContext()
    const [searchValue, setSearchValue] = useState('')

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value)
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()

        onSearch(searchValue)
      }
    }

    const handleClear = () => {
      setSearchValue('')
      onSearch('')
    }

    return (
      <div className="flex-initial relative h-full w-full">
        <input
          ref={ref}
          type="text"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            fontSizeMap[searchInputFontSize],
            themeBackgroundMap.container,
            themeTextColorMap.primary,
            paddingMap[searchInputPadding],
            lineHeightMap[searchInputLeading],
            'pr-11 w-full border-none focus-visible:outline-none'
          )}
          value={searchValue}
        />
        {search === '' ? (
          <Icon
            name="MagnifyingGlassIcon"
            size="md"
            color="info"
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
          />
        ) : (
          <IconButton
            name="XMarkIcon"
            size="md"
            color="info"
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
            onClick={handleClear}
          />
        )}
      </div>
    )
  }
)

export { MultiselectSearch }
